import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import './ProducingKitchensSettings.css';

type Department = { id: number; name: string };
type ProducingKitchen = {
  id: number; name: string; departmentId: number; departmentName: string;
  effectiveFrom: string; effectiveTo: string | null; version: number;
};
type KitchenDraft = { name: string; departmentId: string; effectiveFrom: string; effectiveTo: string };
const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const emptyDraft = (): KitchenDraft => ({ name: '', departmentId: '', effectiveFrom: today(), effectiveTo: '' });

export default function ProducingKitchensSettings({ canEdit }: { canEdit: boolean }) {
  const [kitchens, setKitchens] = useState<ProducingKitchen[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [editing, setEditing] = useState<ProducingKitchen | null>(null);
  const [draft, setDraft] = useState<KitchenDraft>(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshingVersion, setRefreshingVersion] = useState(false);
  const [staleVersion, setStaleVersion] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const load = useCallback(async () => {
    const [kitchenResponse, departmentResponse] = await Promise.all([
      api.get<ProducingKitchen[]>('/producing-kitchens'),
      api.get<Department[]>('/operating-groups/departments/kitchen-settings'),
    ]);
    setKitchens(kitchenResponse.data);
    setDepartments(departmentResponse.data);
    return kitchenResponse.data;
  }, []);
  useEffect(() => {
    load().catch(() => setError('Не вдалося завантажити виробничі кухні.'))
      .finally(() => setLoading(false));
  }, [load]);
  const startEdit = (kitchen: ProducingKitchen) => {
    setEditing(kitchen);
    setDraft({ name: kitchen.name, departmentId: String(kitchen.departmentId), effectiveFrom: kitchen.effectiveFrom, effectiveTo: kitchen.effectiveTo ?? '' });
    setError(''); setMessage(''); setStaleVersion(false);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim() || !draft.departmentId || !draft.effectiveFrom) return;
    if (draft.effectiveTo && draft.effectiveTo <= draft.effectiveFrom) {
      setError('Кінцева дата має бути пізнішою за початкову; межа не включається.'); return;
    }
    setSaving(true); setError(''); setMessage('');
    const body = { departmentId: Number(draft.departmentId), name: draft.name.trim(), effectiveFrom: draft.effectiveFrom, effectiveTo: draft.effectiveTo || null };
    try {
      if (editing) await api.put(`/producing-kitchens/${editing.id}`, { ...body, version: editing.version });
      else await api.post('/producing-kitchens', body);
      await load();
      setEditing(null); setDraft(emptyDraft());
      setMessage(editing ? 'Виробничу кухню оновлено.' : 'Виробничу кухню створено.');
    } catch (e: any) {
      const stale = e?.response?.status === 409 || e?.response?.status === 412;
      setStaleVersion(stale);
      setError(stale
        ? 'Версія кухні застаріла: маршрутизація або генерація кухонних робіт змінила запис. Оновіть версію перед повторним збереженням.'
        : e?.response?.data?.message || 'Не вдалося зберегти виробничу кухню.');
    } finally { setSaving(false); }
  };
  if (loading) return <p role="status">Завантаження виробничих кухонь…</p>;
  return <section aria-label="Виробничі кухні" className="producing-kitchens-settings">
    <p>Виробнича кухня закріплена за підрозділом; періоди дії застосовуються до дати події. Кожному підрозділу призначається не більше однієї виробничої кухні. Записи з історією не видаляються.</p>
    {error && <div role="alert" style={{ color: 'var(--crm-danger)' }}>
      <p>{error}</p>
      {staleVersion && editing && <button type="button" disabled={refreshingVersion || saving} onClick={async () => {
        setRefreshingVersion(true);
        try {
          const freshKitchens = await load();
          const freshKitchen = freshKitchens.find(kitchen => kitchen.id === editing.id);
          if (!freshKitchen) throw new Error('Кухню не знайдено після оновлення.');
          setEditing(freshKitchen); setStaleVersion(false);
          setError('Версію оновлено. Перевірте зміни та збережіть ще раз.');
        } catch (refreshError: any) {
          setError(refreshError?.message || 'Не вдалося оновити кухню. Повторіть завантаження.');
        } finally { setRefreshingVersion(false); }
      }}>{refreshingVersion ? 'Оновлення…' : 'Оновити версію кухні'}</button>}
    </div>}
    {message && <p role="status">{message}</p>}
    {canEdit && <form onSubmit={save} className="producing-kitchens-form">
      <strong style={{ gridColumn: '1 / -1' }}>{editing ? `Редагування: ${editing.name}` : 'Нова виробнича кухня'}</strong>
      <label>Назва<input aria-label="Назва виробничої кухні" required maxLength={120} value={draft.name} disabled={saving} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
      <label>Підрозділ<select aria-label="Підрозділ виробничої кухні" required value={draft.departmentId} disabled={saving || Boolean(editing)} onChange={e => setDraft({ ...draft, departmentId: e.target.value })}>
        <option value="">Оберіть підрозділ</option>{departments.filter(item => item.id === editing?.departmentId || !kitchens.some(kitchen => kitchen.departmentId === item.id)).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label>
      <label>Діє з<input aria-label="Початок дії кухні" type="date" required value={draft.effectiveFrom} disabled={saving} onChange={e => setDraft({ ...draft, effectiveFrom: e.target.value })} /></label>
      <label>Діє до (не включно)<input aria-label="Кінець дії кухні" type="date" min={draft.effectiveFrom} value={draft.effectiveTo} disabled={saving} onChange={e => setDraft({ ...draft, effectiveTo: e.target.value })} /></label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <button type="submit" disabled={saving || !draft.name.trim() || !draft.departmentId || Boolean(draft.effectiveTo && draft.effectiveTo <= draft.effectiveFrom)}>{saving ? 'Збереження…' : editing ? 'Зберегти кухню' : 'Створити кухню'}</button>
        {editing && <button type="button" disabled={saving} onClick={() => { setEditing(null); setDraft(emptyDraft()); }}>Скасувати</button>}
      </div>
    </form>}
    <div className="producing-kitchens-list">
      {kitchens.map(kitchen => <article key={kitchen.id} className="producing-kitchen-card">
        <span><strong>{kitchen.name}</strong> · {kitchen.departmentName}<br /><small>{kitchen.effectiveFrom} — {kitchen.effectiveTo ?? 'без кінцевої дати'}</small></span>
        {canEdit && <button type="button" disabled={saving} onClick={() => startEdit(kitchen)}>Редагувати</button>}
      </article>)}
      {!kitchens.length && <p>Виробничих кухонь ще немає.</p>}
    </div>
  </section>;
}
